import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface LoginPageProps {
  isAdminLogin?: boolean;
}

export default function LoginPage({ isAdminLogin = false }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Mapear usuarios a sus emails correspondientes
      let email = '';
      let redirectPath = '';
      let displayName = '';

      if (username.toLowerCase() === 'balsareny') {
        email = 'balsareny@bodas-epicas.com';
        redirectPath = '/private/gallery';
        displayName = 'Balsareny';
      } else if (username.toLowerCase() === 'balsa') {
        email = 'balsa@bodas-epicas.com';
        redirectPath = '/private/balsa-gallery';
        displayName = 'Balsa';
      } else {
        email = `${username}@bodas-epicas.com`;
        redirectPath = '/private/gallery';
        displayName = username;
      }

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError('Usuario o contraseña incorrectos');
        setIsLoading(false);
        return;
      }

      if (data.session) {
        const sessionData = {
          username,
          name: displayName,
          timestamp: new Date().toISOString(),
          adminMode: isAdminLogin,
        };
        sessionStorage.setItem('privateSession', JSON.stringify(sessionData));

        navigate(redirectPath);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error al iniciar sesión. Intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://static.readdy.ai/image/1e1c36a8fe2eee6efab369c345d12000/bfc8f53255aeb1d0a57e759d04829909.png"
            alt="Bodas Épicas"
            className="h-16 w-auto mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-gray-900 font-['Cormorant_Garamond']">
            {isAdminLogin ? 'Acceso Administración' : 'Acceso Privado'}
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            {isAdminLogin
              ? 'Ingresa tus credenciales de administrador'
              : 'Ingresa tus credenciales para acceder a tu galería'}
          </p>
        </div>

        {/* Formulario de Login */}
        <div className="bg-white rounded-lg shadow-xl p-8 border border-amber-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo Usuario */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                placeholder="Ingresa tu usuario"
                required
              />
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                placeholder="Ingresa tu contraseña"
                required
              />
            </div>

            {/* Mensaje de Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                <i className="ri-error-warning-line mr-2"></i>
                {error}
              </div>
            )}

            {/* Botón de Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber-700 hover:bg-amber-800 text-white font-medium py-3 px-6 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Accediendo...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <i className="ri-login-box-line mr-2"></i>
                  Acceder
                </span>
              )}
            </button>
          </form>

          {/* Volver al inicio */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-sm text-amber-700 hover:text-amber-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Volver al inicio
            </a>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <i className="ri-lock-line mr-1"></i>
          Acceso seguro y privado
        </div>
      </div>
    </div>
  );
}
